import { useState } from 'react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, Button, Input, Label, Select } from '@/components/ui'
import { Fingerprint, Copy, Check, Trash2, Plus } from 'lucide-react'
import { toast } from 'sonner'

type UUIDVersion = 'v4' | 'v1-like'

interface GeneratedUUID {
  id: string
  uuid: string
  copied: boolean
}

export function UUIDGenerator() {
  const [uuids, setUuids] = useState<GeneratedUUID[]>([])
  const [version, setVersion] = useState<UUIDVersion>('v4')
  const [count, setCount] = useState(1)
  const [uppercase, setUppercase] = useState(false)
  const [noDashes, setNoDashes] = useState(false)

  const generateUUIDv4 = (): string => {
    const array = new Uint8Array(16)
    crypto.getRandomValues(array)

    // Set version (4) and variant bits
    array[6] = (array[6] & 0x0f) | 0x40
    array[8] = (array[8] & 0x3f) | 0x80

    const hex = Array.from(array)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')

    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
  }

  const generateUUIDv1Like = (): string => {
    const now = Date.now()
    const array = new Uint8Array(16)
    crypto.getRandomValues(array)

    // Embed timestamp in first 8 bytes
    const timeHex = now.toString(16).padStart(12, '0')
    for (let i = 0; i < 6; i++) {
      array[i] = parseInt(timeHex.slice(i * 2, i * 2 + 2), 16)
    }

    // Set version (1) and variant bits
    array[6] = (array[6] & 0x0f) | 0x10
    array[8] = (array[8] & 0x3f) | 0x80

    const hex = Array.from(array)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')

    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
  }

  const formatUUID = (uuid: string): string => {
    let result = uuid
    if (noDashes) result = result.replace(/-/g, '')
    if (uppercase) result = result.toUpperCase()
    return result
  }

  const generate = () => {
    const newUuids: GeneratedUUID[] = []

    for (let i = 0; i < count; i++) {
      const raw = version === 'v4' ? generateUUIDv4() : generateUUIDv1Like()
      newUuids.push({
        id: crypto.randomUUID(),
        uuid: formatUUID(raw),
        copied: false,
      })
    }

    setUuids((prev) => [...newUuids, ...prev])
    toast.success(`Generated ${count} UUID${count > 1 ? 's' : ''}`)
  }

  const copyUUID = async (id: string) => {
    const uuid = uuids.find((u) => u.id === id)
    if (!uuid) return

    try {
      await navigator.clipboard.writeText(uuid.uuid)
      setUuids((prev) =>
        prev.map((u) => ({
          ...u,
          copied: u.id === id,
        }))
      )
      toast.success('UUID copied')
      setTimeout(() => {
        setUuids((prev) =>
          prev.map((u) => ({ ...u, copied: false }))
        )
      }, 2000)
    } catch {
      toast.error('Failed to copy')
    }
  }

  const copyAll = async () => {
    if (uuids.length === 0) return

    try {
      const text = uuids.map((u) => u.uuid).join('\n')
      await navigator.clipboard.writeText(text)
      toast.success('All UUIDs copied')
    } catch {
      toast.error('Failed to copy')
    }
  }

  const removeUUID = (id: string) => {
    setUuids((prev) => prev.filter((u) => u.id !== id))
  }

  const clearAll = () => {
    setUuids([])
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
            <Fingerprint className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">UUID Generator</h1>
            <p className="text-muted-foreground">Generate universally unique identifiers</p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[350px_1fr]">
        {/* Options */}
        <Card className="glass h-fit">
          <CardHeader>
            <CardTitle>Options</CardTitle>
            <CardDescription>Configure UUID generation</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Version</Label>
              <Select
                value={version}
                onChange={(e) => setVersion(e.target.value as UUIDVersion)}
              >
                <option value="v4">UUID v4 (Random)</option>
                <option value="v1-like">UUID v1-like (Time-based)</option>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Count</Label>
              <div className="flex gap-2">
                {[1, 5, 10, 25].map((n) => (
                  <Button
                    key={n}
                    variant={count === n ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setCount(n)}
                  >
                    {n}
                  </Button>
                ))}
              </div>
            </div>

            <div className="space-y-3 pt-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={uppercase}
                  onChange={(e) => setUppercase(e.target.checked)}
                  className="rounded border-border"
                />
                <span className="text-sm">Uppercase</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={noDashes}
                  onChange={(e) => setNoDashes(e.target.checked)}
                  className="rounded border-border"
                />
                <span className="text-sm">Remove dashes</span>
              </label>
            </div>

            <Button onClick={generate} className="w-full">
              <Plus className="h-4 w-4 mr-2" />
              Generate
            </Button>
          </CardContent>
        </Card>

        {/* Generated UUIDs */}
        <Card className="glass">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Generated UUIDs</CardTitle>
                <CardDescription>{uuids.length} UUID{uuids.length !== 1 ? 's' : ''} generated</CardDescription>
              </div>
              {uuids.length > 0 && (
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={copyAll}>
                    <Copy className="h-3 w-3 mr-1" />
                    Copy All
                  </Button>
                  <Button variant="outline" size="sm" onClick={clearAll}>
                    <Trash2 className="h-3 w-3 mr-1" />
                    Clear
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {uuids.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Fingerprint className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>Click Generate to create UUIDs</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[400px] overflow-y-auto custom-scrollbar">
                {uuids.map((uuid) => (
                  <div
                    key={uuid.id}
                    className="flex items-center gap-2 p-2 rounded-lg bg-muted/30 border border-border group"
                  >
                    <Input
                      value={uuid.uuid}
                      readOnly
                      className="font-mono text-sm flex-1 bg-transparent border-0 focus-visible:ring-0"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="shrink-0"
                      onClick={() => copyUUID(uuid.id)}
                    >
                      {uuid.copied ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => removeUUID(uuid.id)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
